import { memo } from 'react'
import cn from 'classnames'

import { Icon } from '~/shared/ui/Icon'
import { Pressable } from '~/shared/ui/Pressable'

import { VALUE_FIELD_HEIGHT, VALUE_FIELD_ICON_SIZE } from './config'
import type { ValueFieldProps } from './types'

import styles from './ValueField.module.scss'

/**
 * Поле-кнопка «подпись + значение + иконка»: дата, время, город — всё, что выбирается
 * в шторке, а не набирается с клавиатуры.
 *
 * Плавающая подпись (основатель, п.10): пока значения нет, она стоит по центру поля;
 * как только значение появилось — уезжает наверх, значение встаёт под ней. Высота при
 * этом не меняется, поэтому соседние поля не прыгают.
 *
 * Подпись живёт внутри той же зоны, что и значение (`.body`), а не поверх всей карточки:
 * иначе длинная подпись заезжала бы под иконку справа и не совпадала бы по левому краю
 * со значением.
 */
export const ValueField = memo(function ValueField(props: ValueFieldProps) {
  const {
    label,
    value,
    iconName,
    size = 'M',
    tone = 'SECONDARY',
    hasError = false,
    isActive = false,
    ariaLabel,
    onPress,
  } = props

  const isFilled = value !== null && value !== undefined && value !== ''

  return (
    <Pressable as='div' role='button' tabIndex={0} onPress={onPress} ariaLabel={ariaLabel ?? label}>
      <div
        className={cn(
          styles.root,
          styles[`tone_${tone}`],
          isActive && styles.isActive,
          hasError && styles.hasError,
        )}
        style={{ height: VALUE_FIELD_HEIGHT[size] }}
      >
        <div className={styles.body}>
          <span className={cn(styles.label, isFilled && styles.isFloating)}>{label}</span>
          {isFilled && <span className={cn(styles.value, styles[`value_${size}`])}>{value}</span>}
        </div>
        <Icon name={iconName} size={VALUE_FIELD_ICON_SIZE[size]} strokeWidth={1.6} />
      </div>
    </Pressable>
  )
})
